import React, { useEffect, useState } from 'react';
import { api, GiftVoucherDto } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';

// ── Gift Vouchers (admin) ─────────────────────────────────────────────────────
// Read-only visibility into customer-purchased vouchers, plus a manual void (e.g. the purchaser
// disputed the charge, or it was issued in error) — no create/edit here, purchasing only happens
// through the customer "Send a Gift" flow. See IGiftVoucherService for the full shape.

const statusColor = (s: string) => s === 'Captured' ? C.greenMid : s === 'Pending' ? '#EF9F27' : C.textMuted;

export function GiftVouchersPanel() {
    const [vouchers, setVouchers] = useState<GiftVoucherDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [voidingId, setVoidingId] = useState<string | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getGiftVouchersAdmin().then(setVouchers).catch(e => setError(e.message || 'Failed to load vouchers.')).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const voidVoucher = async (id: string, code: string) => {
        if (!window.confirm(`Void voucher ${code}? This can't be undone — it will no longer be redeemable.`)) return;
        setVoidingId(id);
        try { await api.voidGiftVoucher(id); load(); }
        catch (e: any) { setError(e.message || 'Failed to void voucher.'); }
        finally { setVoidingId(null); }
    };

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Gift Vouchers</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Customer-purchased, single-use, service-type-locked vouchers — purchased through the customer "Send a Gift" flow</div>
            </div>

            <Alert message={error} />

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {vouchers.length === 0 && <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No gift vouchers purchased yet.</div>}
                    {vouchers.map((v, i) => (
                        <div key={v.id} style={{
                            padding: '14px 18px',
                            borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                            opacity: v.isVoided ? 0.5 : 1,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, color: C.text }}>{v.code}</span>
                                <span style={{
                                    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                                    background: C.bg, color: statusColor(v.paymentStatus),
                                }}>{v.isVoided ? 'Voided' : v.paymentStatus}</span>
                                {v.redeemedAt && (
                                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: C.greenBg, color: C.green }}>
                                        Redeemed
                                    </span>
                                )}
                                <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 700, color: C.green }}>${v.amount.toFixed(2)}</span>
                            </div>
                            <div style={{ fontSize: 13, color: C.text, marginBottom: 2 }}>
                                {v.serviceTypeName}
                                {v.shreddingRequestType === 'BinDelivery' && v.shreddingSizeName ? ` — ${v.shreddingSizeName}${(v.shreddingEstimatedUnits ?? 1) > 1 ? ` x${v.shreddingEstimatedUnits}` : ''} bin delivery` : ''}
                                {v.shreddingRequestType === 'BinRetrieval' ? ' — bin retrieval' : ''}
                            </div>
                            <div style={{ fontSize: 12, color: C.textSub }}>
                                From {v.purchaserName} to {v.recipientName} ({v.recipientEmail})
                            </div>
                            {v.message && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, fontStyle: 'italic' }}>"{v.message}"</div>}
                            {v.redeemedAt && (
                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
                                    Redeemed {new Date(v.redeemedAt).toLocaleDateString()} by {v.redeemedByName}
                                </div>
                            )}
                            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                                Purchased {new Date(v.createdAt).toLocaleDateString()}
                            </div>
                            {!v.isVoided && !v.redeemedAt && (
                                <Btn variant="outline" onClick={() => voidVoucher(v.id, v.code)} disabled={voidingId === v.id} style={{ marginTop: 8, fontSize: 12, padding: '6px 12px' }}>
                                    {voidingId === v.id ? 'Voiding…' : 'Void'}
                                </Btn>
                            )}
                        </div>
                    ))}
                </Card>
            )}
        </div>
    );
}
